import {useCallback, useId, useMemo, useRef, useState} from 'react'

import {HeroPagination, type HeroPaginationProps} from './HeroPagination/HeroPagination'
import {HeroSequence, type HeroSequenceProps, type HeroPlaybackState} from './HeroSequence/HeroSequence'

import Styles from './Hero.module.css'

export interface HeroProps {
  a11y: {
    title: string
  }
  heroSequence: Omit<
    HeroSequenceProps,
    | 'currentChapterIndex'
    | 'labelledBy'
    | 'onNextChapter'
    | 'onReady'
    | 'onPlaybackChange'
    | 'videoRef'
    | 'playbackStateRef'
    | 'seekRequest'
  >
  heroPagination: Omit<HeroPaginationProps, 'onChange' | 'items' | 'currentChapterIndex' | 'playback'> & {
    items: string[]
  }
}

export const defaultProps: Partial<HeroProps> = {}

export const Hero = (props: HeroProps) => {
  const initializedProps: HeroProps = {...defaultProps, ...props}
  const {a11y, heroSequence, heroPagination} = initializedProps
  const titleId = useId()

  const [currentChapterIndex, setCurrentChapterIndex] = useState<number>(0)
  const [chapterDurations, setChapterDurations] = useState<number[]>([])
  const videoRef = useRef<HTMLVideoElement>(null)
  const playbackStateRef = useRef<HeroPlaybackState | null>(null)
  const [seekRequest, setSeekRequest] = useState(0)
  const [playbackState, setPlaybackState] = useState<HeroPlaybackState>({
    currentTime: 0,
    isPaused: true,
    isPlaying: false,
    isFinished: false,
  })
  const selectChapter = useCallback((index: number) => {
    setCurrentChapterIndex(index)
    setSeekRequest(request => request + 1)
  }, [])
  const togglePlayback = useCallback(async () => {
    const media = videoRef.current
    if (!media) return
    const previousState = playbackStateRef.current
    const shouldPlay = !previousState || previousState.isPaused || previousState.isFinished
    const state = {
      currentTime: previousState?.isFinished ? 0 : (previousState?.currentTime ?? media.currentTime),
      isPaused: !shouldPlay,
      isPlaying: false,
      isFinished: false,
    }
    // A source change pauses the native element without changing the user's playback choice.
    playbackStateRef.current = state
    setPlaybackState(state)

    if (shouldPlay) {
      if (previousState?.isFinished) {
        media.currentTime = 0
        selectChapter(0)
      }
      const source = media.getAttribute('src')
      try {
        await media.play()
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        if (videoRef.current === media && media.getAttribute('src') === source) {
          const pausedState = {...(playbackStateRef.current ?? state), isPaused: true, isPlaying: false}
          playbackStateRef.current = pausedState
          setPlaybackState(pausedState)
        }
        window.reportError(error)
      }
    } else {
      media.pause()
    }
  }, [selectChapter])
  const playback = useMemo(
    () => ({
      ...playbackState,
      elapsed: playbackState.currentTime - (heroSequence.video?.chapterTimestamps[currentChapterIndex] ?? 0),
      onToggle: togglePlayback,
    }),
    [playbackState, heroSequence.video, currentChapterIndex, togglePlayback],
  )
  const items = useMemo(
    () => heroPagination.items.map((label, index) => ({label, duration: chapterDurations[index] ?? 0})),
    [heroPagination.items, chapterDurations],
  )

  return (
    <div className={Styles['Hero']}>
      <h2 id={titleId} className="visually-hidden">
        {a11y.title}
      </h2>
      <HeroSequence
        {...heroSequence}
        labelledBy={titleId}
        currentChapterIndex={currentChapterIndex}
        onNextChapter={setCurrentChapterIndex}
        onReady={setChapterDurations}
        videoRef={videoRef}
        playbackStateRef={playbackStateRef}
        seekRequest={seekRequest}
        onPlaybackChange={setPlaybackState}
      />
      <HeroPagination
        {...heroPagination}
        currentChapterIndex={currentChapterIndex}
        onChange={selectChapter}
        items={items}
        playback={heroSequence.video ? playback : undefined}
      />
    </div>
  )
}
